import { useEffect, useRef } from 'react';
import { sendHeadsup } from '../services/api';
import { getVisitorIdentitySnapshot } from '../services/identity';
import { storageService } from '../services/storage';
import { useCartStore } from '../store/cartStore';

/**
 * Hook that monitors tab visibility and sends headsup notifications
 * when user leaves or returns to the tab while having items in cart.
 * 
 * - tab_switched: Fired when user switches to another tab (visibilitychange)
 * - tab_closed: Fired when user closes the tab or navigates away (pagehide)
 * - tab_returned: Fired when user returns to a previously opened tab
 */
export function useTabVisibility(): void {
    const { cart } = useCartStore();
    const wasHidden = useRef(false);
    const hasCartItems = cart.items.length > 0;

    useEffect(() => {
        // Only run in browser
        if (typeof document === 'undefined') {
            return;
        }

        const handleVisibilityChange = () => {
            // Only notify if cart has items
            if (!hasCartItems) {
                // Track visibility state even without cart items
                // so we know if they return with items added later
                wasHidden.current = document.hidden;
                return;
            }

            try {
                const visitorIdentity = getVisitorIdentitySnapshot();
                const savedCustomer = storageService.getCustomer();
                const hasUserInfo = !!(savedCustomer?.name || savedCustomer?.address);

                const payload = {
                    visitorId: visitorIdentity?.id,
                    customerName: savedCustomer?.name || undefined,
                    customerAddress: savedCustomer?.address || undefined,
                    cartItems: cart.items,
                    // Only include visit count if user doesn't have previous order info
                    visitCount: !hasUserInfo ? visitorIdentity?.visitCount : undefined,
                };

                if (document.hidden) {
                    // Tab is now hidden (user switched to another tab)
                    wasHidden.current = true;
                    sendHeadsup({
                        ...payload,
                        eventType: 'tab_switched',
                    });
                } else if (wasHidden.current) {
                    // Tab is now visible and was previously hidden (user returned)
                    wasHidden.current = false;
                    sendHeadsup({
                        ...payload,
                        eventType: 'tab_returned',
                    });
                }
            } catch (error) {
                // Silently ignore - headsup is non-critical
                console.log('[Headsup] Error sending tab visibility headsup, continuing...', error);
            }
        };

        // Use pagehide for tab close detection (more reliable than beforeunload)
        const handlePageHide = () => {
            if (!hasCartItems) {
                return;
            }

            try {
                const visitorIdentity = getVisitorIdentitySnapshot();
                const savedCustomer = storageService.getCustomer();
                const hasUserInfo = !!(savedCustomer?.name || savedCustomer?.address);

                const payload = {
                    eventType: 'tab_closed' as const,
                    visitorId: visitorIdentity?.id,
                    customerName: savedCustomer?.name || undefined,
                    customerAddress: savedCustomer?.address || undefined,
                    cartItems: cart.items,
                    visitCount: !hasUserInfo ? visitorIdentity?.visitCount : undefined,
                };

                // Use sendBeacon for reliability on page unload
                const isProduction = window.location.hostname === 'delifeed.com' || window.location.hostname === 'www.delifeed.com';
                const isAdmin = localStorage.getItem('admin-pass');
                
                if (isProduction && !isAdmin && navigator.sendBeacon) {
                    navigator.sendBeacon(
                        '/api/telegram/headsup',
                        JSON.stringify(payload)
                    );
                }
            } catch (error) {
                // Silently ignore
                console.log('[Headsup] Error sending page hide headsup, continuing...', error);
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('pagehide', handlePageHide);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('pagehide', handlePageHide);
        };
    }, [hasCartItems, cart.items]);
}
